import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button,
  Chip,
  Divider,
  IconButton,
  Modal,
  Box,
  Typography,
  Grid,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import {
  calculateAgeOnDate,
  canCalculateAgeOnDate,
  getGenotypeDisplayParts
} from 'src/util/util';
import { toLocalDateFromPickerOrImport } from 'src/util/partialDate';
import RadioInput from 'src/components/UI/input/RadioInput';
import TextInput from 'src/components/UI/input/TextInput';
import { CLINICAL_RECORD_DIALOG_WIDTH } from 'src/components/UI/dialog/CustomDialog';
import ClinicalNoteDeleteModal from '../diagnoses-menu/ClinicalNoteDeleteModal';
import { CONSTANTS } from 'src/components/Constants';
import VariantList from './VariantList';
import GeneValidityBlock from './GeneValidityBlock';
import useGeneValidity from './useGeneValidity';
import { geneLookupKey } from './geneValidity';
import { useFreshEntity } from 'src/hooks/useFreshEntity';
import { ModalDateFieldProvider } from 'src/components/UI/input/ModalDateFieldContext';
import UnsavedChangesDialog from 'src/components/UI/dialog/UnsavedChangesDialog';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  ChangedField,
  UnsavedChangeCount,
  useChangedFields,
  useUnsavedFormGuard
} from 'src/components/UI/input/changed-field';

// Build an NCBI Gene URL for a stored genotype. Pattern A (preferred): direct
// gene-page link by Entrez ID when `id` is in "NCBIGene:NNN" form. Pattern B
// (fallback): NCBI search by HGNC symbol — NCBI resolves it to the canonical
// gene page. Constructed at click time, never persisted.
const buildNcbiGeneUrl = (genotype) => {
  if (!genotype) return null;
  const id = String(genotype.id || '');
  if (id.startsWith('NCBIGene:')) {
    const num = id.slice('NCBIGene:'.length).trim();
    if (num) {
      return `https://www.ncbi.nlm.nih.gov/gene/${encodeURIComponent(num)}`;
    }
  }
  const { symbol } = getGenotypeDisplayParts(genotype);
  if (symbol) {
    const term = `${symbol}[Symbol] AND Homo sapiens[Organism]`;
    return `https://www.ncbi.nlm.nih.gov/gene/?term=${encodeURIComponent(
      term
    )}`;
  }
  return null;
};

const GenotypeModal = ({
  open,
  onClose,
  genotype,
  onSave,
  currentUserName = '',
  onDeleteGenotype,
  isDisabled,
  patientDob,
  staffMemberNames = [],
  fetchFreshEntity,
  onFresh,
  refetchKey,
  onShowHistory,
  // The open reclassification events about this person (TGCLOUD-1899), by variantId.
  reclassificationEvents = []
}) => {
  const t = useAutoTranslation();

  const [clinicallyConfirmed, setClinicallyConfirmed] = useState(false);
  const [patientReported, setPatientReported] = useState(false);
  const [displayOnPedigree, setDisplayOnPedigree] = useState(true);
  const [dateRecorded, setDateRecorded] = useState('');
  const [recordedBy, setRecordedBy] = useState('');
  const [recordedAge, setRecordedAge] = useState('');
  const [carrierStatus, setCarrierStatus] = useState('');
  const [comment, setComment] = useState('');
  const [variants, setVariants] = useState([]);
  // Preserved on round-trip so already-saved ClinVar selections aren't lost
  // when the modal is closed. The picker UI itself was removed.
  const [selectedVariantIds, setSelectedVariantIds] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);

  // UNSAVED CHANGES (TGCLOUD-1902): each field against the genotype as opened,
  // by value (a radio's 'true' is the true on the record). The variants, with
  // their interpretations and reviews, are one field on screen.
  const changes = useChangedFields({
    baseline: {},
    values: {
      dateRecorded,
      recordedBy,
      recordedAge,
      carrierStatus,
      clinicallyConfirmed,
      patientReported,
      displayOnPedigree,
      comment,
      variants
    },
    fields: [
      'variants',
      'dateRecorded',
      'recordedBy',
      'recordedAge',
      'carrierStatus',
      'clinicallyConfirmed',
      'patientReported',
      'displayOnPedigree',
      'comment'
    ],
    compare: {
      clinicallyConfirmed: { kind: 'flag' },
      patientReported: { kind: 'flag' },
      displayOnPedigree: { kind: 'flag' }
    },
    // Worked out from the date of birth and the date: never gold.
    exclude: (name) => name === 'recordedAge' && !!patientDob && !!dateRecorded
  });
  const { resetBaseline } = changes;
  // Read by the background refetch below: it must not re-seed over changes.
  // Only once the form HAS been seeded: before that, its defaults differ from
  // the empty baseline and would block the first seed.
  const unsavedRef = useRef(0);
  const seededRef = useRef(false);
  unsavedRef.current = changes.unsavedCount;
  useUnsavedFormGuard(open ? changes.unsavedCount : 0);

  const { entity: liveGenotype, isFetching } = useFreshEntity({
    open,
    fetcher: fetchFreshEntity,
    fallback: genotype,
    onFresh,
    refetchKey
  });

  // GenCC validity for the gene this modal is about. Read-only reference data,
  // deliberately outside the form state above, and only fetched while the modal
  // is open.
  //
  // Keyed on geneLookupKey, NOT on getGenotypeDisplayParts().symbol. The latter
  // is a DISPLAY helper that falls back to the whole free-text `name`, so a
  // record labelled 'BRCA1 c.5047G>T' would be looked up under that string and
  // come back "no GenCC submission for this gene" — a false clinical finding,
  // because we never established which gene it is. geneLookupKey returns null
  // for anything that is not an approved HGNC symbol, and the block then shows
  // "gene not identified" instead of a verdict. Integrations that record genes
  // outside HGNC nomenclature by design (the Sapio import) land there.
  const genccLookupKey = geneLookupKey(liveGenotype || genotype);
  const { response: genccResponse, loading: genccLoading } = useGeneValidity(
    genccLookupKey || '',
    { enabled: open && !!genccLookupKey }
  );

  const yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  // The age on the calendar day of the date recorded (calculateAgeOnDate).
  // `dateRecorded` is a `type: String` column with two writers: adding a
  // genotype and the inline capture write a 'YYYY-MM-DD', and this modal's
  // picker writes its Date, which reaches the column as the local-anchored
  // instant's "…Z" string. toLocalDateFromPickerOrImport reads each by its
  // writer, and the date field shows that same day. A date before the birth
  // has no age (calculateAge's Math.abs gave a positive one), and clears a
  // saved one; a date that cannot be read keeps it (canCalculateAgeOnDate).
  const computeAge = useCallback(
    (date) => calculateAgeOnDate(patientDob, date),
    [patientDob]
  );

  // Named so "Discard changes" can re-run it. This modal stays mounted after
  // closing (GenotypesMenu never clears selectedGenotype), so a discard that
  // only closed would leave the abandoned edits on screen at the next open.
  const seedFromGenotype = useCallback(() => {
    if (!liveGenotype) return;
    const seeded = {
      clinicallyConfirmed: liveGenotype.clinicallyConfirmed || false,
      patientReported: liveGenotype.patientReported || false,
      displayOnPedigree: liveGenotype.displayOnPedigree ?? true,
      dateRecorded: liveGenotype.dateRecorded || '',
      recordedBy: liveGenotype.recordedBy || currentUserName,
      carrierStatus: liveGenotype.carrierStatus || '',
      comment: liveGenotype.comment || '',
      // Always seed from the saved age. The sync effect below recomputes when
      // the user changes the date, but never overwrites a saved age because
      // no date was recorded.
      recordedAge: liveGenotype.recordedAge ?? '',
      variants: Array.isArray(liveGenotype.variants)
        ? liveGenotype.variants
        : []
    };
    setClinicallyConfirmed(seeded.clinicallyConfirmed);
    setPatientReported(seeded.patientReported);
    setDisplayOnPedigree(seeded.displayOnPedigree);
    setDateRecorded(seeded.dateRecorded);
    setRecordedBy(seeded.recordedBy);
    setCarrierStatus(seeded.carrierStatus);
    setComment(seeded.comment);
    setRecordedAge(seeded.recordedAge);
    setVariants(seeded.variants);
    setSelectedVariantIds(
      Array.isArray(liveGenotype.selectedClinVarIds)
        ? liveGenotype.selectedClinVarIds
        : []
    );
    // The genotype as opened: what every field is compared with.
    resetBaseline(seeded);
    seededRef.current = true;
  }, [liveGenotype, currentUserName, resetBaseline]);

  useEffect(() => {
    // The guard stays on the EFFECT, not inside the callback: a background
    // refetch must not clobber changes in progress, but an explicit discard
    // must be able to re-seed regardless.
    if (seededRef.current && unsavedRef.current > 0) return;
    seedFromGenotype();
  }, [seedFromGenotype]);

  const [unsavedPrompt, setUnsavedPrompt] = useState(false);

  // Every user-initiated close routes through here. The post-save and
  // post-delete closes do NOT: GenotypesMenu flips `open` itself, so they can
  // never raise this prompt.
  const requestClose = () => {
    if (changes.unsavedCount > 0) {
      setUnsavedPrompt(true);
      return;
    }
    onClose();
  };

  const discardAndClose = () => {
    setUnsavedPrompt(false);
    seedFromGenotype();
    onClose();
  };

  useEffect(() => {
    if (!patientDob) return;
    // Only apply a date-derived age — never erase a saved age because the
    // date field is empty. The inline-capture path stores age without a
    // date, and we must preserve it on re-hydration. A date before the birth
    // does erase it.
    if (!canCalculateAgeOnDate(patientDob, dateRecorded)) return;
    const nextAge = computeAge(dateRecorded);
    setRecordedAge((prev) =>
      String(nextAge) === String(prev) ? prev : nextAge
    );
  }, [patientDob, computeAge, dateRecorded]);

  const handleClinicianInterpretationChange = useCallback(
    (variantId, value) => {
      setVariants((prev) =>
        prev.map((v) =>
          v.variantId === variantId
            ? {
                ...v,
                clinicianInterpretation: value,
                clinicianInterpretationBy: currentUserName || '',
                clinicianInterpretationAt: new Date().toISOString()
              }
            : v
        )
      );
    },
    [currentUserName]
  );

  // APPENDS the acknowledgement. Never edits or removes an earlier one: the
  // audit question is who stood behind what and when, so a reclassification by
  // a second clinician has to read as exactly that rather than as the first
  // opinion vanishing.
  //
  // The determination is applied HERE, from the dialog, so deciding and
  // reviewing are one act — and so Cancel in the dialog leaves nothing behind.
  // Each entry snapshots what was reviewed, which is what lets the marker
  // re-raise if any of the three values moves later.
  const handleReviewDiscrepancy = useCallback(
    (variantId, { determination, note } = {}) => {
      const at = new Date().toISOString();
      setVariants((prev) =>
        prev.map((v) =>
          v.variantId === variantId
            ? {
                ...v,
                clinicianInterpretation: determination,
                clinicianInterpretationBy: currentUserName || '',
                clinicianInterpretationAt: at,
                discrepancyReviews: [
                  ...(Array.isArray(v.discrepancyReviews)
                    ? v.discrepancyReviews
                    : []),
                  {
                    reviewedBy: currentUserName || '',
                    reviewedAt: at,
                    note: note || '',
                    determination,
                    labAtReview: v.labInterpretation || '',
                    clinvarAtReview: v.clinvarInterpretation || ''
                  }
                ]
              }
            : v
        )
      );
    },
    [currentUserName]
  );

  const handleSaveClick = () => {
    const payload = {
      ...(liveGenotype || genotype),
      clinicallyConfirmed,
      patientReported,
      displayOnPedigree,
      dateRecorded,
      recordedBy,
      // null, not undefined, which JSON drops: PUT /genotypes merges what it
      // is sent, so a missing key kept the old age and a blanked one (a date
      // before the birth) could never be cleared.
      recordedAge: recordedAge === '' ? null : Number(recordedAge),
      carrierStatus,
      comment: comment?.trim() || undefined,
      variants,
      selectedClinVarIds: selectedVariantIds
    };
    // TGCLOUD-1766: the Desktop private flag is retired. A legacy value on the
    // stored genotype is not echoed back, so Cloud neither sets nor re-sends it.
    delete payload.isPrivate;
    onSave(payload);
  };

  const handleOpenModal = () => {
    setOpenDeleteModal(true);
  };

  const handleCloseModal = () => {
    setOpenDeleteModal(false);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await onDeleteGenotype((liveGenotype || genotype).id);
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setDeleteLoading(false);
      handleCloseModal();
    }
  };

  return (
    <ModalDateFieldProvider>
      <UnsavedChangesDialog
        open={unsavedPrompt}
        count={changes.unsavedCount}
        onCancel={() => setUnsavedPrompt(false)}
        onDiscard={discardAndClose}
      />
      <ClinicalNoteDeleteModal
        loading={deleteLoading}
        open={openDeleteModal}
        handleCloseModal={handleCloseModal}
        onConfirm={handleDelete}
        title="Are you sure you want to delete this Genotype ?"
        content={t(
          'Warning: This action will permanently delete this Genotype. Once deleted, this information will be permanently removed and cannot be restored.'
        )}
      />
      <Modal open={open} onClose={requestClose}>
        <Box
          data-va-anchor="pedigree.genotypes.editDialog"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            // Match the shared clinical-record / disease modal width so the
            // genotype editor reads as part of the same family of dialogs.
            width: CLINICAL_RECORD_DIALOG_WIDTH,
            maxWidth: '95vw',
            height: 700,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 1,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              backgroundColor: 'secondary.main',
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
              flexShrink: 0
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold', textAlign: 'left', color: '#fff' }}
            >
              {t('Edit Genotype')}
              {isFetching && (
                <Typography
                  component="span"
                  sx={{ ml: 1, fontSize: 12, fontWeight: 400, opacity: 0.85 }}
                >
                  {t('(refreshing…)')}
                </Typography>
              )}
            </Typography>
            <IconButton
              size="small"
              onClick={requestClose}
              sx={{ color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 2, pt: 3, overflowY: 'auto', flex: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1,
                  mb: 0.5
                }}
              >
                <Typography variant="body1">
                  <strong>{t('Symbol:')}</strong>{' '}
                  {getGenotypeDisplayParts(liveGenotype || genotype).symbol ||
                    t('N/A')}
                </Typography>
                {(() => {
                  const target = liveGenotype || genotype;
                  const url = buildNcbiGeneUrl(target);
                  if (!url) return null;
                  const id = String(target?.id || '');
                  const label = id.startsWith('NCBIGene:')
                    ? id
                    : t('NCBI Gene');
                  return (
                    <Chip
                      label={label}
                      clickable
                      onClick={() =>
                        window.open(url, '_blank', 'noopener,noreferrer')
                      }
                      sx={{
                        fontSize: '1rem',
                        fontWeight: 400,
                        border: 'none',
                        color: '#1a7a8a',
                        backgroundColor: 'rgba(43, 169, 191, 0.15)',
                        borderRadius: '10px',
                        height: 'auto',
                        py: 0.5
                      }}
                    />
                  );
                })()}
              </Box>
              {getGenotypeDisplayParts(liveGenotype || genotype)
                .description && (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 0.5
                  }}
                >
                  {
                    getGenotypeDisplayParts(liveGenotype || genotype)
                      .description
                  }
                </Typography>
              )}
            </Box>

            {/* GenCC curated validity for this gene (TGCLOUD-1794).
                Read-only reference data: it is derived from the gene symbol and
                never enters the modal's form state, so it cannot be clobbered by
                useFreshEntity's re-seed or falsely mark the record dirty. */}
            <GeneValidityBlock
              response={genccResponse}
              loading={genccLoading}
              geneIdentified={!!genccLookupKey}
            />

            <ChangedField
              component={Box}
              sx={{ mb: 2 }}
              name="variants"
              changed={changes.changed.variants}
              formatWas={() => undefined}
            >
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                {t('Variants')}
              </Typography>
              <VariantList
                variants={variants}
                onClinicianInterpretationChange={
                  handleClinicianInterpretationChange
                }
                onReviewDiscrepancy={handleReviewDiscrepancy}
                reviewerName={currentUserName}
                onSourceTestClick={(detail) => {
                  window.dispatchEvent(
                    new CustomEvent('open-source-test-record', { detail })
                  );
                }}
                disabled={isDisabled}
                reclassificationEvents={reclassificationEvents}
              />
            </ChangedField>

            {/* Date / Recorded By */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <ChangedField
                component={Grid}
                size={6}
                name="dateRecorded"
                changed={changes.changed.dateRecorded}
              >
                <TextInput
                  label="Date Recorded"
                  name="dateRecorded"
                  type="date"
                  value={toLocalDateFromPickerOrImport(dateRecorded)}
                  onChange={(val) => {
                    setDateRecorded(val);
                    if (canCalculateAgeOnDate(patientDob, val)) {
                      setRecordedAge(computeAge(val));
                    }
                  }}
                  disableFuture
                />
              </ChangedField>
              <ChangedField
                component={Grid}
                size={6}
                name="recordedBy"
                changed={changes.changed.recordedBy}
              >
                <TextInput
                  label="Recorded By"
                  name="recordedBy"
                  type="autocomplete"
                  value={recordedBy}
                  options={staffMemberNames}
                  onChange={(newValue) => {
                    const label = newValue ? newValue.label || newValue : '';
                    setRecordedBy(label);
                  }}
                />
              </ChangedField>
              <ChangedField
                component={Grid}
                size={6}
                name="recordedAge"
                changed={changes.changed.recordedAge}
                disabled={!!patientDob && !!dateRecorded}
              >
                <TextInput
                  label="Recorded Age"
                  name="recordedAge"
                  type="number"
                  value={recordedAge ?? ''}
                  // Disabled (muted) only when an age is actually being
                  // auto-calculated, i.e. DoB AND date recorded both set.
                  // Editable when either is missing so a known age can be
                  // recorded directly.
                  disabled={!!patientDob && !!dateRecorded}
                  onChange={
                    patientDob && dateRecorded
                      ? undefined
                      : (e) => {
                          const val = e.target.value;
                          const parsed = val === '' ? '' : Number(val);
                          if (val === '' || !Number.isNaN(parsed)) {
                            setRecordedAge(val === '' ? '' : parsed);
                          }
                        }
                  }
                />
              </ChangedField>
              <ChangedField
                component={Grid}
                size={6}
                name="carrierStatus"
                changed={changes.changed.carrierStatus}
              >
                <TextInput
                  label="Carrier Status"
                  name="carrierStatus"
                  type="select"
                  value={carrierStatus}
                  onChange={(e) => {
                    setCarrierStatus(e.target.value);
                  }}
                  options={[
                    { label: 'None', value: '' },
                    ...CONSTANTS.carrierStatuses,
                    { label: 'Affected', value: 'Affected' }
                  ]}
                />
                {/* Guidance for values that need disambiguating — currently only
                    "Homozygote or Compound Heterozygote", now that the narrower
                    "Homozygote" also appears in the list (TGCLOUD-1655).
                    Rendered here rather than on the option itself for two reasons:
                    `label` is also what draws on the pedigree and shows in the
                    record, and SelectInput is a NATIVE select, so option text
                    appears in the closed field too — a hint folded into the label
                    would sit in the field permanently once selected. */}
                {CONSTANTS.carrierStatuses
                  .filter((o) => o.hint)
                  .map((o) => (
                    <Typography
                      key={o.value}
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        color: 'text.secondary'
                      }}
                    >
                      {t(o.label)} — {t(o.hint)}
                    </Typography>
                  ))}
              </ChangedField>
              <ChangedField
                component={Grid}
                size={6}
                name="clinicallyConfirmed"
                changed={changes.changed.clinicallyConfirmed}
                variant="control"
                kind="flag"
              >
                <RadioInput
                  label="Clinically Confirmed"
                  name="clinicallyConfirmed"
                  onChange={(e) => {
                    setClinicallyConfirmed(e.target.value);
                  }}
                  options={yesNoOptions}
                  value={clinicallyConfirmed}
                  row
                />
              </ChangedField>
              <ChangedField
                component={Grid}
                size={6}
                name="patientReported"
                changed={changes.changed.patientReported}
                variant="control"
                kind="flag"
              >
                <RadioInput
                  label="Patient Reported"
                  name="patientReported"
                  onChange={(e) => {
                    setPatientReported(e.target.value);
                  }}
                  options={yesNoOptions}
                  value={patientReported}
                  row
                />
              </ChangedField>
              <ChangedField
                component={Grid}
                size={6}
                name="displayOnPedigree"
                changed={changes.changed.displayOnPedigree}
                variant="control"
                kind="flag"
              >
                <RadioInput
                  label="Display on Pedigree"
                  name="displayOnPedigree"
                  onChange={(e) => {
                    setDisplayOnPedigree(e.target.value);
                  }}
                  options={yesNoOptions}
                  value={displayOnPedigree}
                  row
                />
              </ChangedField>
            </Grid>

            <ChangedField
              component={Box}
              sx={{ mb: 2 }}
              name="comment"
              changed={changes.changed.comment}
            >
              <TextInput
                type="textarea"
                label="Comments"
                name="comment"
                value={comment || ''}
                onChange={(e) => {
                  setComment(e.target.value);
                }}
                rows={3}
              />
            </ChangedField>

            {/* Bottom action buttons */}
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleOpenModal}
                disabled={isDisabled || deleteLoading}
                endIcon={
                  deleteLoading ? <CircularProgress size={15} /> : undefined
                }
              >
                {t('Delete')}
              </Button>
              <UnsavedChangeCount count={changes.count} />
              <Button
                variant="outlined"
                color="secondary"
                onClick={requestClose}
              >
                {t('Cancel')}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSaveClick}
                disabled={isDisabled}
              >
                {t('Save')}
              </Button>
            </Box>
            {onShowHistory && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <IconButton
                  size="small"
                  title={t('Record history')}
                  onClick={onShowHistory}
                >
                  <HistoryIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </ModalDateFieldProvider>
  );
};

export default GenotypeModal;
